import React, { useContext, useState, useEffect } from 'react';
import { APIContext } from '../../../Contexts/APIContext';
import DropdownViewModel from '../../../Contexts/ViewModels/DropdownViewModel';
import CallTableViewModel from '../../../ViewModels/CallTableViewModel';
import QAScorecardFilterViewModel from './ViewModels/QAScorecardFilterViewModel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

interface IFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function QAScorecardFilterDialog(props: IFiltersDialogProps) {
    const { get } = useContext(APIContext);
    const [scorecardTypeOptions, setScorecardTypeOptions] = useState<string[]>([]);
    const [templateOptions, setTemplateOptions] = useState<DropdownViewModel[]>([]);
    const [scorecardFilter, setScorecardFilter] = useState<QAScorecardFilterViewModel>(props.callTableViewModel.filters.qaScorecardFilter);

    useEffect(() => {
        if (props.open) {
            setScorecardFilter(props.callTableViewModel.filters.qaScorecardFilter);
        }
    }, [props.open])

    useEffect(() => {
        getScorecardTypeOptions();
    }, [scorecardFilter.showInactiveScorecards]);

    useEffect(() => {
        getTemplateOptions();
    }, [scorecardFilter.scorecardType, scorecardFilter.showInactiveScorecards]);

    const getScorecardTypeOptions = () => {
        get('ScorecardManagement', `GetAllScorecardTypeOptions?client=${props.callTableViewModel.filters.client}&showInactive=${scorecardFilter.showInactiveScorecards}`, (data) => {
            setScorecardTypeOptions(data);
        });
    }

    const getTemplateOptions = () => {
        const scorecardTypeParam = scorecardFilter.scorecardType && scorecardFilter.scorecardType !== 'All'
            ? `&scorecardType=${encodeURIComponent(scorecardFilter.scorecardType)}`
            : '';

        get('ScorecardManagement', `GetAllTemplateOptions?client=${props.callTableViewModel.filters.client}${scorecardTypeParam}&showInactive=${scorecardFilter.showInactiveScorecards}`, (data) => {
            setTemplateOptions(data);
        });
    }

    const handleTypeSelect = (scorecardType: string) => {
        setScorecardFilter({
            ...scorecardFilter,
            scorecardType,
            templateId: 0,
            templateName: ''
        });
    }

    const handleShowInactiveToggle = (showInactiveScorecards: boolean) => {
        setScorecardFilter({
            ...scorecardFilter,
            showInactiveScorecards,
            scorecardType: 'All',
            templateId: 0,
            templateName: ''
        });
    }

    const handleTemplateSelect = (templateId: number) => {
        const template = templateOptions.find(f => f.id === templateId);

        setScorecardFilter({
            ...scorecardFilter,
            templateId,
            templateName: template ? template.label : ''
        });
    }

    const confirm = () => {
        const newCallTableViewModel = { ...props.callTableViewModel };

        newCallTableViewModel.filters.qaScorecardFilter = scorecardFilter;

        props.setCallTableViewModel({
            ...newCallTableViewModel
        })

        props.setOpen(false);
    }

    const filterAvailable = scorecardTypeOptions.length > 0;

    return (
        <Dialog onClose={() => props.setOpen(false)} open={props.open} fullWidth maxWidth="sm">
            <DialogTitle>QA Scorecard Filter</DialogTitle>
            <DialogContent>
                {filterAvailable ?
                    <Grid container height="100%">
                        <Grid size={12}>
                            <FormControlLabel control={
                                <Checkbox
                                    checked={scorecardFilter.showInactiveScorecards}
                                    onChange={(e) => handleShowInactiveToggle(e.target.checked)}
                                />}
                                label="Show Inactive Scorecards"
                            />
                        </Grid>
                        <Grid size={12}>
                            <InputLabel>Scorecard Type</InputLabel>
                            <TextField
                                select
                                name="scorecardType"
                                value={scorecardFilter.scorecardType}
                                onChange={(e) => handleTypeSelect(e.target.value)}
                                fullWidth
                            >
                                <MenuItem value="All">All</MenuItem>
                                {scorecardTypeOptions.map((scorecardType: string) => {
                                    return (<MenuItem key={scorecardType} value={scorecardType}>{scorecardType}</MenuItem>);
                                })}
                            </TextField>
                        </Grid>
                        <Grid size={12}>
                            <InputLabel>Template</InputLabel>
                            <TextField
                                select
                                name="templateId"
                                value={scorecardFilter.templateId}
                                onChange={(e) => handleTemplateSelect(Number(e.target.value))}
                                fullWidth
                            >
                                <MenuItem value={0}>Is Any</MenuItem>
                                {templateOptions.map((template: DropdownViewModel) => {
                                    return (<MenuItem key={template.id} value={template.id}>{template.label}</MenuItem>);
                                })}
                            </TextField>
                        </Grid>
                    </Grid>
                    :
                    <p>This AI Filter Is Not Used By This Client</p>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={() => props.setOpen(false)}>
                    Cancel
                </Button>
                {filterAvailable &&
                    <Button onClick={confirm} color="primary">
                        Confirm
                    </Button>
                }
            </DialogActions>
        </Dialog >
    )
}
